"use client";

import { useEffect } from "react";

/**
 * MobileSplashScreen — dismisses the NATIVE Capacitor splash. That is its whole
 * job now.
 *
 * ⚠️ It used to also render its own full-screen overlay: an 80px `/favicon.png`
 * plus a spinning ring, held for a fixed 2 seconds. Two problems with that:
 *
 *  1. WHITE-LABEL WAS BROKEN. The logo was hardcoded to InsuraCentral's
 *     favicon, so an MRFG (or any white-label) user refreshing the app saw the
 *     InsuraCentral mark for two seconds before their own branding appeared.
 *     Desktop never had this bug.
 *  2. It DUPLICATED the real loader. The inline <script> in layout.tsx builds
 *     `#__loading-screen` before React hydrates — same 80px logo, same spinning
 *     ring, same white ground — except it reads `insura_branding_cache` from
 *     localStorage, so it shows the CORRECT logo and brand color instantly, and
 *     it tears down when auth actually finishes (SmoothLoadingScreen watches
 *     `isInitializing`) instead of after an arbitrary 2s.
 *
 * So the native app now shows exactly what desktop shows on a refresh, branded
 * correctly. Capacitor's `launchAutoHide` is false, so the native splash must
 * still be dismissed explicitly — that happens here, and the inline overlay is
 * already painted underneath by the time it does.
 *
 * Deliberately mounted outside the provider stack so a provider error can't
 * strand users on a frozen splash.
 */
export function MobileSplashScreen() {
  useEffect(() => {
    const isNative = (() => {
      if (typeof window === "undefined") return false;
      try {
        return (window as any).Capacitor?.isNativePlatform?.() === true;
      } catch {
        return false;
      }
    })();

    if (!isNative) return;

    (async () => {
      try {
        const { SplashScreen } = await import("@capacitor/splash-screen");
        await SplashScreen.hide({ fadeOutDuration: 200 });
      } catch {
        // Plugin missing or already hidden — the web overlay is showing either
        // way, so there is nothing to recover from.
      }
    })();
  }, []);

  return null;
}

export default MobileSplashScreen;
